<script setup lang="ts">
import { computed, ref } from 'vue';

import IconCheckCircle from '~/assets/icons/IconCheckCircle.vue';
import IconFileText from '~/assets/icons/IconFileText.vue';
import StatusCard from '~/components/shared/StatusCard.vue';
import useGroup from '~/hooks/useGroup';
import { FinancingFlowApi } from '~/services/financing-flow-api/financing-flow.types';
import { RefundOrigin } from '~/services/receipt-model-api/receipt-model-api.types';

import RefundModal from '../../components/RefundModal.vue';
import { TRANSFER_REFUND_GROUP, TRANSFER_REFUND_TERMINAL_STATUSES } from '../../constants';
import useReceiptModel from '../../queries/receipt-model/useReceiptModel';
import useRefundTransfers from '../../queries/useRefundTransfers';
import TransfersTable from '../paymentModel/TransfersTable.vue';

const TRANSFERS_TITLE = 'Transferências';

interface Props {
  financing: FinancingFlowApi.FinancingV2;
}

const props = defineProps<Props>();

const completedAt = computed(() => props.financing.section_statuses.payment_model?.completed_at || undefined);
const submittedAt = computed(() => props.financing.section_statuses.payment_model?.submitted_at || undefined);

const projectId = computed(() => props.financing.project_id);
const {
  data: receiptModelData,
  error: receiptModelError,
  isFetching: isFetchingReceiptModel,
  isLoading: isLoadingReceiptModel,
} = useReceiptModel(projectId);

const isLoading = computed(() => isLoadingReceiptModel.value || isFetchingReceiptModel.value);

const { hasAccess: canAccessRefund } = useGroup([TRANSFER_REFUND_GROUP]);
const refundTransfersMutation = useRefundTransfers();
const isRefundModalVisible = ref(false);

const areAllTransfersTerminal = computed(() => {
  const transfers = receiptModelData.value?.transfers;
  if (!transfers?.length) return false;

  return transfers.every((transfer) => TRANSFER_REFUND_TERMINAL_STATUSES.includes(
    transfer.status as typeof TRANSFER_REFUND_TERMINAL_STATUSES[number],
  ));
});

/** Habilitado por padrão; desabilita só quando todas as transferências estão paid/cancelled. */
const canRefundTransfers = computed(() => !areAllTransfersTerminal.value);

const refundTooltip = computed(() => (
  areAllTransfersTerminal.value
    ? 'Não há o que estornar: todas as transferências já estão pagas ou canceladas.'
    : ''
));

const handleRefundClick = () => {
  isRefundModalVisible.value = true;
};

const handleRefundConfirm = async (data: { origin: RefundOrigin; reason: string }) => {
  try {
    await refundTransfersMutation.mutateAsync({
      origin: data.origin,
      projectId: projectId.value,
      reason: data.reason,
    });
    isRefundModalVisible.value = false;
  } catch {
    // Error handled by mutation
  }
};
</script>
<template>
  <div class="payment-model-step">
    <div class="mb-4">
      <div class="row g-3">
        <div class="status-card-col">
          <StatusCard
            title="Submetido em"
            :datetime="submittedAt"
            :icon="IconFileText"
            type="informative"
          />
        </div>

        <div class="status-card-col">
          <StatusCard
            title="Concluído em"
            :datetime="completedAt"
            :icon="IconCheckCircle"
            type="positive"
          />
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="mt-4">
      <h4 class="mb-3">{{ TRANSFERS_TITLE }}</h4>
      <div class="placeholder-glow">
        <div class="placeholder col-12 mb-2" />
        <div class="placeholder col-8 mb-2" />
        <div class="placeholder col-6" />
      </div>
    </div>

    <div v-else-if="receiptModelError" class="mt-4">
      <h4 class="mb-3">{{ TRANSFERS_TITLE }}</h4>
      <div class="alert alert-warning">
        <p class="mb-0">Não foi possível carregar as informações de transferências.</p>
      </div>
    </div>

    <div v-else-if="receiptModelData" class="mt-4">
      <div class="transfers-header mb-3">
        <h4 class="mb-0">{{ TRANSFERS_TITLE }}</h4>
        <div
          v-if="canAccessRefund"
          :data-tooltip="refundTooltip"
          class="tooltip-wrapper"
          :class="{ 'has-tooltip': !canRefundTransfers }"
        >
          <button
            type="button"
            class="btn btn-sm refund-btn"
            :class="canRefundTransfers ? 'btn-outline-danger' : 'btn-outline-secondary'"
            :disabled="!canRefundTransfers || refundTransfersMutation.isPending.value"
            @click="handleRefundClick"
          >
            <span class="refund-btn-full">ESTORNO</span>
            <span class="refund-btn-short">ESTORNO</span>
          </button>
        </div>
      </div>

      <TransfersTable v-if="receiptModelData.transfers?.length" :receipt-model-data="receiptModelData" />
      <div v-else class="alert alert-info">
        <p class="mb-0">Nenhuma transferência encontrada.</p>
      </div>
    </div>

    <div v-else class="mt-4">
      <h4 class="mb-3">{{ TRANSFERS_TITLE }}</h4>
      <div class="alert alert-info">
        <p class="mb-0">Dados ainda não preenchidos</p>
      </div>
    </div>

    <RefundModal
      v-model="isRefundModalVisible"
      title="Estorno"
      primary-button-text="Confirmar Estorno"
      placeholder="Descreva o motivo do estorno das transferências..."
      field-label="Motivo do Estorno"
      :min-length="10"
      :max-length="255"
      :is-loading="refundTransfersMutation.isPending.value"
      @confirm="handleRefundConfirm"
    />
  </div>
</template>

<style scoped lang="scss">
.payment-model-step {
  .status-card-col {
    @media (min-width: 768px) {
      flex: 0 0 auto;
      width: 50%;
    }
  }

  .badge {
    font-size: 0.75rem;
  }
}

.transfers-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.refund-btn {
  .refund-btn-short {
    display: none;
  }

  @media (max-width: 575.98px) {
    .refund-btn-full {
      display: none;
    }

    .refund-btn-short {
      display: inline;
    }
  }
}

.tooltip-wrapper {
  display: inline-block;
  position: relative;

  &.has-tooltip {
    &::before {
      content: attr(data-tooltip);
      position: absolute;
      bottom: calc(100% + 8px);
      right: 0;
      left: auto;
      transform: none;
      background-color: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 10px 14px;
      border-radius: 6px;
      font-size: 0.8rem;
      font-weight: 500;
      z-index: 1050;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.15s ease-in-out, visibility 0.15s ease-in-out;
      min-width: 200px;
      max-width: 280px;
      width: max-content;
      white-space: normal;
      text-align: center;
      line-height: 1.4;
      word-wrap: break-word;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

      @media (max-width: 768px) {
        max-width: 220px;
        font-size: 0.75rem;
        padding: 8px 12px;
      }
    }

    &::after {
      content: '';
      position: absolute;
      bottom: calc(100% + 2px);
      right: 20px;
      left: auto;
      transform: none;
      border: 6px solid transparent;
      border-top-color: rgba(0, 0, 0, 0.9);
      z-index: 1050;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.15s ease-in-out, visibility 0.15s ease-in-out;
    }

    &:hover {
      &::before,
      &::after {
        opacity: 1;
        visibility: visible;
      }
    }
  }
}
</style>
